import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Column } from 'react-table';
import { Col, Container, Row } from 'react-bootstrap';
import PaginatedVehicleTable from '../../components/VehicleTable/PaginatedVehicleTable';
import { columnsData } from '../../components/VehicleTable/columnsData';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import toaster from '../../utils/toaster';
import { GTMsendEvent } from '../../utils/trackingGA4';
import {
	ExportFormat,
	FileExportMenu,
} from 'src/components/FileExportMenu/FileExportMenu';
import { downloadCSV, downloadXLSX } from 'src/utils';
import { VehicleStateEnum } from 'src/api';
import { useAuth } from 'src/hooks/useAuth';
import { useApiClient } from 'src/hooks/useApiClient';
import { toServerColumns } from 'src/vehicleList/columns';
import { buildVehicleExportData } from 'src/vehicleList/exportData';
import { ExportProgress, fetchAllPages } from 'src/vehicleList/fetchAllPages';
import {
	availabilityOptions,
	stateOptions,
} from 'src/vehicleList/filterOptions';
import { usePaginatedVehicles } from 'src/vehicleList/usePaginatedVehicles';

const QUICKO_SCOPE = {
	quotable: true,
	statoCorrente: [VehicleStateEnum.DISPONIBILE, VehicleStateEnum.OPZIONATO],
};

const QuickoVehiclesPage = () => {
	const { t } = useTranslation();
	const { canViewPromoNote } = useAuth();
	const { apiClient } = useApiClient();

	const [exportProgress, setExportProgress] = useState<ExportProgress | null>(
		null
	);
	const isExporting = exportProgress !== null;

	const { items, loading, error, request, tableProps } = usePaginatedVehicles({
		cacheKey: 'quicko',
		scope: QUICKO_SCOPE,
		baseParams: {
			extractModel: true,
			sortBy: 'disponibili',
			sortDirection: 'asc',
		},
	});

	useEffect(() => {
		if (error) {
			console.error('Error fetching vehicles:', error);
			toaster.error(i18next.t('error_loading_data'));
		}
	}, [error]);

	const handleExportButton = async (format: ExportFormat) => {
		setExportProgress({ loaded: 0, total: tableProps.totalCount });

		try {
			const all = await fetchAllPages(
				(params) =>
					apiClient.motornetControllerService.getPlatformVehicles(params),
				request,
				setExportProgress
			);

			const exportData = buildVehicleExportData(all, { canViewPromoNote });

			if (format === 'xlsx') {
				downloadXLSX(exportData, 'Lista Veicoli QUICKO');
			} else {
				downloadCSV(exportData, 'Lista Veicoli QUICKO');
			}

			GTMsendEvent({
				event: 'CTA_download_file',
				evtCat: 'lista_veicoli_promo',
				evtAct: 'download',
				evtLab: `quicko_vehicles_list.${format}`,
			});
		} catch (err: any) {
			toaster.error(err?.message || i18next.t('error_loading_data'));
		} finally {
			setExportProgress(null);
		}
	};

	const columns = useMemo(
		() =>
			toServerColumns(
				columnsData.filter(
					(c) =>
						!['massive_update_selector', 'display_reporter'].includes(
							c.accessor
						)
				)
			) as Column<Object>[],
		[]
	);

	const filterOptions = useMemo(
		() => ({
			stato_corrente: stateOptions(QUICKO_SCOPE),
			stato_disp: availabilityOptions(),
		}),
		[]
	);

	return (
		<Container fluid>
			<Row>
				<Col md={6} className="display-6">
					{t('quicko-vehicles-list')}
				</Col>

				<Col md={6} className="mt-4 mt-md-0 text-md-end">
					<FileExportMenu
						isDisabled={loading || isExporting}
						allowedFormats={['csv', 'xlsx']}
						handleExport={handleExportButton}
						label={
							exportProgress
								? `${exportProgress.loaded.toLocaleString(
										'it-IT'
								  )} / ${exportProgress.total.toLocaleString('it-IT')}`
								: undefined
						}
					/>
				</Col>
			</Row>

			<ErrorBoundary>
				<PaginatedVehicleTable
					{...tableProps}
					columns={columns}
					data={items}
					loading={loading}
					error={error}
					filterOptions={filterOptions}
					hiddenColumns={['id', 'prezzo_listino']}
					skeletonProps={{ rows: 10, cols: 6 }}
				></PaginatedVehicleTable>
			</ErrorBoundary>
		</Container>
	);
};

export default QuickoVehiclesPage;
